/**
 * @fileoverview Shared detection schemas for world element extraction V3.
 *
 * These schemas define the lightweight output format shared by both the core
 * and rare detector agents. They intentionally omit type-specific fields to
 * keep detection fast and focused — enrichment happens in the detailer step.
 *
 * - `DetectedWorldElementSchema` — A single detected element (name, type, subtype, classification, briefNote)
 * - `DetectorOutputSchema` — Array wrapper used as the output schema for both detectors
 * - `ElementClassificationEnum` — critical / major / minor importance levels
 * - `WorldElementSubtypeEnum` — All subtypes across all 7 element types
 */

import { z } from 'zod';
import { WorldElementTypeV3Enum } from './world-element-v3';
import { ALL_WORLD_ELEMENT_SUBTYPES } from '@/domains/world-building/constants';

// =============================================================================
// ENUMS
// =============================================================================

/**
 * Classification of element importance to the story.
 */
export const ElementClassificationEnum = z.enum(['critical', 'major', 'minor']);
export type ElementClassification = z.infer<typeof ElementClassificationEnum>;

/**
 * Zod enum for element subtypes, derived from the canonical constant.
 * NOT a local definition — references ALL_WORLD_ELEMENT_SUBTYPES from constants.ts.
 */
export const WorldElementSubtypeEnum = z.enum(ALL_WORLD_ELEMENT_SUBTYPES);
export type WorldElementSubtypeValue = z.infer<typeof WorldElementSubtypeEnum>;

// =============================================================================
// DETECTED ELEMENT
// =============================================================================

/**
 * A single detected world element — lightweight, no type-specific fields.
 * Used as the output item for both the core and rare detector agents.
 */
export const DetectedWorldElementSchema = z.object({
  name: z.string().describe('The element name as it appears in the text, or a descriptive name if unnamed.'),
  type: WorldElementTypeV3Enum.describe('The world element type.'),
  element: WorldElementSubtypeEnum.describe('The specific element sub-type.'),
  classification: ElementClassificationEnum.describe(
    'Importance to the story. ' +
    '"critical" = central to plot, appears repeatedly. ' +
    '"major" = appears in multiple scenes, important to characters/subplots. ' +
    '"minor" = mentioned, contributes to world texture.'
  ),
  briefNote: z.string().describe(
    'A single sentence capturing what this element IS in the story. ' +
    'Not a description — a reference note for later enrichment. ' +
    'E.g., "The ancestral sword of House Stark, made of Valyrian steel."'
  ),
});

export type DetectedWorldElement = z.infer<typeof DetectedWorldElementSchema>;

// =============================================================================
// DETECTOR OUTPUT
// =============================================================================

/**
 * Output schema shared by both detector agents.
 */
export const DetectorOutputSchema = z.object({
  elements: z.array(DetectedWorldElementSchema).describe(
    'All detected world-building elements from the text.'
  ),
});

export type DetectorOutput = z.infer<typeof DetectorOutputSchema>;
